import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Phone,
  Send,
  User,
  Mail,
  MessageSquare,
  Building,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { siteConfig, locations } from '@/data/siteData';
import emailjs from '@emailjs/browser';

// EmailJS Configuration
// To set up: 1) Create free account at https://www.emailjs.com
//            2) Add an Email Service (Gmail/Outlook) 
//            3) Create an Email Template with variables: {{from_name}}, {{from_email}}, {{phone}}, {{company}}, {{service}}, {{message}}
//            4) Replace these values with your own from the EmailJS dashboard
const EMAILJS_SERVICE_ID = 'service_felixinfo';
const EMAILJS_TEMPLATE_ID = 'template_contact';
const EMAILJS_PUBLIC_KEY = 'YOUR_EMAILJS_PUBLIC_KEY'; // Replace with your EmailJS public key
const CONTACT_EMAIL = 'ajith@anraone.com';

export const ContactForm = ({ variant = 'default' }) => {
  const ref = useRef(null);
  const formRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Send via EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone || 'Not provided',
          company: formData.company || 'Not provided',
          service: formData.service || 'Not specified',
          message: formData.message,
          to_email: CONTACT_EMAIL,
          reply_to: formData.email,
        },
        EMAILJS_PUBLIC_KEY
      );

      toast.success('Thank you! Your message has been sent. We will contact you shortly.');
      setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      // Fallback: open mailto link
      const subject = encodeURIComponent(`Contact Form: ${formData.name} - ${formData.service || 'General Inquiry'}`);
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nCompany: ${formData.company}\nService: ${formData.service}\n\nMessage:\n${formData.message}`
      );
      window.open(`mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`, '_blank');
      toast.success('Thank you! Your email client has been opened to send the message.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (variant === 'simple') {
    return (
      <section className="py-16 bg-gray-50" ref={ref}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="max-w-2xl mx-auto bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
              Get In Touch
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Have questions? We&apos;d love to hear from you.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <Input
                  name="email"
                  type="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <Input
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
              />
              <Textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleChange}
                className="min-h-[100px]"
                required
              />
              <Button type="submit" className="w-full bg-[#0066CC] hover:bg-[#0055AA] text-white" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 lg:py-28 relative overflow-hidden" style={{ backgroundColor: '#0066CC' }} ref={ref}>
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info - Dark bg (blue), white icons */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 style={{ color: 'white', fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '1.5rem', lineHeight: '1.2' }}>
              Let&apos;s Build Something Great Together
            </h2>
            <p style={{ color: 'white', fontSize: '1.125rem', marginBottom: '2rem', maxWidth: '28rem' }}>
              Ready to transform your business with innovative technology solutions? 
              Get in touch with our experts today.
            </p>

            {/* Contact Details - White bg boxes, blue icons */}
            <div className="space-y-4 mb-10">
              <a
                href={`tel:${siteConfig.phone}`}
                className="flex items-center gap-4 p-4 rounded-xl bg-white hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-[#0066CC]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Call Us</p>
                  <p className="text-lg font-semibold text-gray-900">{siteConfig.phone}</p>
                </div>
              </a>

              <a
                href={`mailto:${siteConfig.email}`}
                className="flex items-center gap-4 p-4 rounded-xl bg-white hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#0066CC]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email Us</p>
                  <p className="text-lg font-semibold text-gray-900">{siteConfig.email}</p>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-white">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#0066CC]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Working Hours</p>
                  <p className="text-lg font-semibold text-gray-900">Mon - Sat: 9AM - 6PM</p>
                </div>
              </div>
            </div>

            {/* Locations Preview */}
            <div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>Our Locations</p>
              <div className="flex flex-wrap gap-2">
                {locations.slice(0, 6).map((loc, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 rounded-full bg-white text-sm font-medium text-[#0066CC]"
                  >
                    {loc.city.split(' ')[0]}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Form - Light bg, blue icons */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Request a Free Quote
              </h3>
              <p className="text-gray-600 mb-8">
                Fill in the details and our team will get back to you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#0066CC]" />
                    <Input
                      name="name"
                      placeholder="Full Name *"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10 h-12 bg-gray-50"
                      required
                    />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#0066CC]" />
                    <Input
                      name="email"
                      type="email"
                      placeholder="Email Address *"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 h-12 bg-gray-50"
                      required
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#0066CC]" />
                    <Input
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10 h-12 bg-gray-50"
                    />
                  </div>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#0066CC]" />
                    <Input
                      name="company"
                      placeholder="Company Name"
                      value={formData.company}
                      onChange={handleChange}
                      className="pl-10 h-12 bg-gray-50"
                    />
                  </div>
                </div>

                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full h-12 px-4 rounded-lg bg-gray-50 border border-gray-200 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0066CC]"
                >
                  <option value="">Select Service Interested In</option>
                  <option value="ict">ICT Solutions</option>
                  <option value="software">Software Solutions</option>
                  <option value="cloud">Cloud & Data Centre</option>
                  <option value="cyber">Cyber Security</option>
                  <option value="other">Other</option>
                </select>

                <div className="relative">
                  <MessageSquare className="absolute left-3 top-4 w-5 h-5 text-[#0066CC]" />
                  <Textarea
                    name="message"
                    placeholder="Tell us about your requirements... *"
                    value={formData.message}
                    onChange={handleChange}
                    className="pl-10 min-h-[120px] bg-gray-50 resize-none"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-[#0066CC] hover:bg-[#0055AA] text-white font-semibold h-14"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2 text-white" />
                      Submit Request
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting, you agree to our Privacy Policy and Terms of Service.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
